import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import './Products.css';
import useProducts from '../hooks/useProducts';
import DeleteConfirmationDialog from './DeleteConfirmationDialog';
import GoldenNotification from './GoldenNotification';
import ProductImagesStockHubModal from './ProductImagesStockHubModal';
import SaleSettingsModal from './SaleSettingsModal';
import CustomerReviewsModal from './CustomerReviewsModal';
import AdminProductCard from './AdminProductCard';
import AdminLoading from './AdminLoading';
import { getAuthHeaders, extractCreatorId, getCreatorDisplayName } from '../utils/authUtils';
import {
  hasAccess,
  canDeleteProduct,
  canSetProductFeatured,
} from '../constants/teamAccessPermissions';
import { getMongoProductId } from '../utils/productMongoId';
import { productService } from '../../services/api';
import { parseThrownAdminError, isTeamRouteAuthorizationError } from '../utils/adminApiError';
import { getApiUrl } from '../../config/environment';
import { getPrimaryProductImageUrl } from '../../utils/productImages';
import useCategories from '../hooks/useCategories';
import {
  productBelongsToCategoryId,
  sortMainCategoriesForDisplay,
  createSlug,
} from '../../utils/categoryUtils';
import { readProductStatusFlags } from '../utils/productStatusFlags';
import { getProductStorefrontPath } from '../../utils/productSeoUrl';

/** Stable id for filtering (Mongo _id / id) */
function productIdStr(p) {
  if (!p) return '';
  return String(p._id ?? p.id ?? '');
}

function productHasAnyStock(p) {
  if (!p?.stock) return false;
  if (!p.sizes) {
    return Object.values(p.stock).some((stock) => stock > 0);
  }
  return Object.values(p.stock).some((colorStock) =>
    Object.values(colorStock).some((stock) => stock > 0)
  );
}

function findCategoryNodeInTree(roots, categoryId) {
  const needle = String(categoryId ?? '').trim();
  if (!needle || !Array.isArray(roots)) return null;
  let found = null;
  const walk = (nodes) => {
    for (const node of nodes || []) {
      if (!node) continue;
      if (String(node._id ?? node.id) === needle) {
        found = node;
        return;
      }
      walk(node.children);
      if (found) return;
    }
  };
  walk(roots);
  return found;
}

function collectCategoryAndDescendantIds(categoryNode) {
  const ids = new Set();
  const walk = (node) => {
    if (!node) return;
    const id = node._id ?? node.id;
    if (id != null && String(id).trim() !== '') ids.add(String(id));
    (node.children || []).forEach(walk);
  };
  walk(categoryNode);
  return ids;
}

/** Match product to admin category filter (Mongo id, tree branch, or legacy slug). */
function productMatchesAdminCategoryFilter(product, filterValue, categoryRoots) {
  if (!filterValue || filterValue === 'all') return true;
  const fv = String(filterValue).trim();

  const node = findCategoryNodeInTree(categoryRoots, fv);
  if (node) {
    for (const id of collectCategoryAndDescendantIds(node)) {
      if (productBelongsToCategoryId(product, id)) return true;
    }
    const nodeName = String(node.name || '').toLowerCase();
    const nodeSlug = createSlug(node.name);
    const mainCat = String(product.mainCategory || '').toLowerCase();
    if (nodeName && mainCat && (mainCat === nodeName || createSlug(mainCat) === nodeSlug)) {
      return true;
    }
  }

  if (productBelongsToCategoryId(product, fv)) return true;

  const legacy = fv.toLowerCase();
  const mainCat = String(product.mainCategory || '').toLowerCase();
  const catName =
    typeof product.category === 'object'
      ? String(product.category?.name || '').toLowerCase()
      : String(product.category || '').toLowerCase();
  return (
    mainCat === legacy ||
    createSlug(mainCat) === legacy ||
    createSlug(catName) === legacy ||
    catName.includes(legacy)
  );
}

function getTotalStock(product) {
  if (!product?.stock) return 0;
  if (typeof product.stock === 'number') return Number(product.stock) || 0;
  try {
    let total = 0;
    const stockEntries =
      product.stock instanceof Map ? product.stock.entries() : Object.entries(product.stock);
    for (const [, sizeMap] of stockEntries) {
      const sizeEntries =
        sizeMap instanceof Map ? sizeMap.entries() : Object.entries(sizeMap || {});
      for (const [, quantity] of sizeEntries) {
        total += Number(quantity) || 0;
      }
    }
    return total;
  } catch {
    return 0;
  }
}

/** Matches Products stock filter "low-stock" (≤5 units on some variant) */
function isLowStockProduct(product) {
  if (!product.stock) return false;
  if (!product.sizes) {
    return Object.values(product.stock).some((stock) => stock > 0 && stock <= 5);
  }
  return Object.values(product.stock).some((colorStock) =>
    Object.values(colorStock).some((stock) => stock > 0 && stock <= 5)
  );
}

const Products = ({ currentUser, onNavigate, onEditProduct, navigationParams = {} }) => {
  const isTeamMember = currentUser?.role === 'team';
  const isMainAdmin = currentUser?.role === 'admin';
  const allowFeaturedProduct = canSetProductFeatured(currentUser);
  const { 
    products, 
    loading, 
    pagination,
    refetch, 
    updateProduct,
    deleteProduct,
    clearDemoProducts
  } = useProducts();
  const { categories: adminCategoryTree } = useCategories();

  const adminCategoryFilterOptions = useMemo(() => {
    const mains = sortMainCategoriesForDisplay(
      Array.isArray(adminCategoryTree) ? adminCategoryTree : []
    );
    const options = [];
    const walk = (node, depth) => {
      if (!node) return;
      const id = node._id ?? node.id;
      if (id == null) return;
      const prefix = depth > 0 ? `${'— '.repeat(depth)}` : '';
      options.push({ value: String(id), label: `${prefix}${node.name || 'Category'}` });
      (node.children || []).forEach((child) => walk(child, depth + 1));
    };
    mains.forEach((main) => walk(main, 0));
    return options;
  }, [adminCategoryTree]);
  const [currentProductPage, setCurrentProductPage] = useState(1);
  const productsPerPage = 16;
  const [clearingDemo, setClearingDemo] = useState(false);
  
  // Delete confirmation and notification states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationData, setNotificationData] = useState({});
  
  // Enhanced deletion states
  const [deleting, setDeleting] = useState(null);
  const [clearingAll, setClearingAll] = useState(false);
  
  /** Unified gallery + stock hub (replaces separate image & stock modals) */
  const [inventoryHubProduct, setInventoryHubProduct] = useState(null);
  
  // Sale settings modal state
  const [showSaleSettings, setShowSaleSettings] = useState(false);
  const [selectedProductForSale, setSelectedProductForSale] = useState(null);
  
  // Customer reviews modal state
  const [showReviewsModal, setShowReviewsModal] = useState(false);
  const [selectedProductForReviews, setSelectedProductForReviews] = useState(null);
  
  // Handle clearing demo products
  const handleClearDemoProducts = async () => {
    if (!window.confirm('Are you sure you want to delete all demo/test products? This action cannot be undone!')) {
      return;
    }

    try {
      setClearingDemo(true);
      const result = await clearDemoProducts();
      alert(`Successfully deleted ${result.deleted} demo products!`);
      // Refresh the products list
      refetch();
    } catch (error) {
      console.error('Error clearing demo products:', error);
      alert('Error clearing demo products: ' + error.message);
    } finally {
      setClearingDemo(false);
    }
  };

  // Enhanced product deletion with Cloudinary cleanup
  const handleDeleteProduct = async (product, productName, productImages) => {
    if (!canDeleteProduct(currentUser, product)) {
      setNotificationData({
        type: 'error',
        title: 'Delete Not Allowed',
        message:
          'You can only delete products you created, and your account needs Products → Delete (or Create) permission.',
        duration: 6000,
      });
      setShowNotification(true);
      return;
    }

    const productId = getMongoProductId(product);
    if (!productId) {
      setNotificationData({
        type: 'error',
        title: 'Delete Failed',
        message: 'Invalid product ID. Refresh the page and try again.',
        duration: 5000,
      });
      setShowNotification(true);
      return;
    }

    const imageCount = productImages ? productImages.length : 0;

    const confirmed = window.confirm(
      `🗑️ Delete Product: "${productName}"\n\n` +
      `This will:\n` +
      `• Delete the product from database\n` +
      `• Delete ${imageCount} image(s) from Cloudinary\n` +
      `• This action cannot be undone!\n\n` +
      `Are you sure you want to continue?`
    );

    if (!confirmed) return;

    setDeleting(productId);

    try {
      console.log('🗑️ Deleting product with Cloudinary cleanup:', { productId, productName, imageCount });

      const result = await productService.deleteProduct(productId);

      if (result?.success) {
        const imagesDeleted =
          result.data?.deletedProduct?.imagesDeleted ??
          result.data?.imagesDeleted ??
          imageCount;
        setNotificationData({
          type: 'success',
          title: 'Product Deleted Successfully!',
          message: `"${productName}" has been deleted${imagesDeleted != null ? ` along with ${imagesDeleted} image(s) from Cloudinary` : ''}.`,
          duration: 5000,
        });
        setShowNotification(true);
        await refetch();
      } else {
        const msg = result?.message || result?.error || 'Failed to delete product. Please try again.';
        setNotificationData({
          type: 'error',
          title: 'Delete Failed',
          message: msg,
          duration: 5000,
        });
        setShowNotification(true);
      }
    } catch (error) {
      const { message, permissionKey, status } = parseThrownAdminError(error);
      console.error('❌ Delete error:', message, { permissionKey, status });
      let userMessage = message || 'Failed to delete product. Please try again.';
      if (isTeamRouteAuthorizationError(message)) {
        userMessage =
          'The API server still blocks team users on DELETE /admin/products (admin-only route). ' +
          'Products → Delete in the panel is saved, but the backend must allow role team on that route and redeploy api.superwear.net. ' +
          'See docs/BACKEND_PROMPT_TEAM_PRODUCT_DELETE.md for your developer.';
      } else if (status === 403 && permissionKey === 'Products|Delete') {
        userMessage +=
          ' Ask your admin to enable Products → Delete for your account.';
      }
      setNotificationData({
        type: 'error',
        title: 'Delete Failed',
        message: userMessage,
        duration: 7000,
      });
      setShowNotification(true);
    } finally {
      setDeleting(null);
    }
  };

  // Clear all products with Cloudinary cleanup
  const handleClearAllProducts = async () => {
    const confirmed = window.confirm(
      `⚠️ DANGER: Clear ALL Products?\n\n` +
      `This will:\n` +
      `• Delete ALL products from database\n` +
      `• Delete ALL images from Cloudinary\n` +
      `• This action cannot be undone!\n\n` +
      `Type "DELETE ALL" to confirm:`
    );

    if (!confirmed) return;

    const doubleConfirm = prompt('Type "DELETE ALL" to confirm:');
    if (doubleConfirm !== 'DELETE ALL') {
      setNotificationData({
        type: 'warning',
        title: 'Operation Cancelled',
        message: 'Confirmation failed. Operation cancelled.',
        duration: 3000
      });
      setShowNotification(true);
      return;
    }

    setClearingAll(true);

    try {
      console.log('🗑️ Clearing all products with Cloudinary cleanup...');
      
      const response = await fetch(getApiUrl('/admin/products/clear-all'), {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      const result = await response.json();

      if (result.success) {
        console.log('✅ All products cleared successfully:', result.data);
        
        setNotificationData({
          type: 'success',
          title: 'All Products Cleared!',
          message: `Successfully deleted ${result.data.productsDeleted} products and ${result.data.imagesDeleted} images from Cloudinary.`,
          duration: 7000
        });
        setShowNotification(true);
        
        // Refresh the products list
        await refetch();
      } else {
        console.error('❌ Clear all failed:', result.message);
        setNotificationData({
          type: 'error',
          title: 'Clear All Failed',
          message: result.message || 'Failed to clear all products. Please try again.',
          duration: 5000
        });
        setShowNotification(true);
      }
    } catch (error) {
      console.error('❌ Clear all error:', error);
      setNotificationData({
        type: 'error',
        title: 'Clear All Error',
        message: 'Network error occurred. Please check your connection and try again.',
        duration: 5000
      });
      setShowNotification(true);
    } finally {
      setClearingAll(false);
    }
  };

  // Delete images only (keep product)
  const handleDeleteImagesOnly = async (productId, productName, productImages) => {
    const imageCount = productImages ? productImages.length : 0;
    
    if (imageCount === 0) {
      setNotificationData({
        type: 'warning',
        title: 'No Images',
        message: 'This product has no images to delete.',
        duration: 3000
      });
      setShowNotification(true);
      return;
    }

    const confirmed = window.confirm(
      `🖼️ Delete Images Only: "${productName}"\n\n` +
      `This will:\n` +
      `• Delete ${imageCount} image(s) from Cloudinary\n` +
      `• Keep the product in database\n` +
      `• This action cannot be undone!\n\n` +
      `Are you sure you want to continue?`
    );

    if (!confirmed) return;

    setDeleting(productId);

    try {
      console.log('🗑️ Deleting images only:', { productId, productName, imageCount });
      
      const response = await fetch(getApiUrl(`/cloudinary/products/${productId}/images`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (result.success) {
        console.log('✅ Images deleted successfully:', result.data);
        
        setNotificationData({
          type: 'success',
          title: 'Images Deleted Successfully!',
          message: `All images for "${productName}" have been deleted from Cloudinary. Product remains in database.`,
          duration: 5000
        });
        setShowNotification(true);
        
        // Refresh the products list
        await refetch();
      } else {
        console.error('❌ Delete images failed:', result.message);
        setNotificationData({
          type: 'error',
          title: 'Delete Images Failed',
          message: result.message || 'Failed to delete images. Please try again.',
          duration: 5000
        });
        setShowNotification(true);
      }
    } catch (error) {
      console.error('❌ Delete images error:', error);
      setNotificationData({
        type: 'error',
        title: 'Delete Images Error',
        message: 'Network error occurred. Please check your connection and try again.',
        duration: 5000
      });
      setShowNotification(true);
    } finally {
      setDeleting(null);
    }
  };

  const handleOpenInventoryHub = (product) => {
    setInventoryHubProduct(product);
  };

  const handleCloseInventoryHub = () => {
    setInventoryHubProduct(null);
  };

  const handleStockUpdated = async () => {
    await refetch();
  };
  
  // Enhanced Filters (from old admin panel)
  const [stockFilter, setStockFilter] = useState('all'); // 'all', 'in-stock', 'out-of-stock', 'low-stock'
  const [categoryFilter, setCategoryFilter] = useState('all'); // 'all' or specific category
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'active', 'inactive', 'featured', 'new', 'sale'
  const [searchQuery, setSearchQuery] = useState(''); // Search by name, ID, description
  const [priceFilter, setPriceFilter] = useState('all'); // 'all', 'low', 'medium', 'high'
  const [createdByFilter, setCreatedByFilter] = useState('all'); // 'all', 'me', or creator userId
  const [likedFilter, setLikedFilter] = useState('all'); // 'all', 'liked', 'not-liked'
  const [likedProducts, setLikedProducts] = useState(new Set()); // Set of liked product IDs
  const [wishlistLoading, setWishlistLoading] = useState(false);

  /** From Stock Alerts panel: null | 'all' low-stock | one product id */
  const [stockAlertFilter, setStockAlertFilter] = useState(null);
  const productsGridTopRef = useRef(null);

  // Handle navigation params (e.g., from dashboard chart clicks, global header search)
  useEffect(() => {
    if (navigationParams.filter === 'liked') {
      setLikedFilter('liked');
    }
    const pre = navigationParams.prefillSearch;
    if (pre != null && String(pre).trim() !== '') {
      setSearchQuery(String(pre).trim());
    }
  }, [navigationParams]);

  // Fetch wishlist data to get liked products
  useEffect(() => {
    fetchLikedProducts();
  }, []);

  // Re-fetch liked products when products data changes
  useEffect(() => {
    if (products && products.length > 0) {
      fetchLikedProducts();
    }
  }, [products]);

  // Debug liked products state
  useEffect(() => {
    console.log('📦 Products - Liked Products State:', likedProducts);
    console.log('📦 Products - Liked Products Size:', likedProducts.size);
    console.log('📦 Products - Liked Products Array:', Array.from(likedProducts));
  }, [likedProducts]);

  // Admin wishlist service function
  const adminWishlistService = {
    // Get wishlist analytics from admin analytics endpoint
    getWishlistAnalytics: async () => {
      try {
        const token = localStorage.getItem('adminToken') || localStorage.getItem('authToken');
        
        console.log('🔄 Calling admin analytics endpoint...');
        const response = await fetch(getApiUrl('/admin/analytics'), {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        console.log('📥 Admin Analytics Response:', data);
        return data;
      } catch (error) {
        console.error('Admin analytics service error:', error);
        throw error;
      }
    },

    // Get detailed wishlist analytics
    getDetailedWishlistAnalytics: async () => {
      try {
        const token = localStorage.getItem('adminToken') || localStorage.getItem('authToken');
        
        console.log('🔄 Calling detailed wishlist analytics...');
        const response = await fetch(getApiUrl('/admin/wishlists/analytics'), {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        console.log('📥 Detailed Wishlist Analytics Response:', data);
        return data;
      } catch (error) {
        console.error('Detailed wishlist analytics error:', error);
        throw error;
      }
    },

    // Get all user wishlists
    getAllUserWishlists: async () => {
      try {
        const token = localStorage.getItem('adminToken') || localStorage.getItem('authToken');
        
        console.log('🔄 Getting all user wishlists...');
        const response = await fetch(getApiUrl('/admin/wishlists?limit=100'), {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        console.log('📥 All User Wishlists Response:', data);
        return data;
      } catch (error) {
        console.error('All user wishlists error:', error);
        throw error;
      }
    }
  };

  const fetchLikedProducts = async () => {
    try {
      setWishlistLoading(true);
      console.log('🔄 Fetching liked products for Products page from localStorage...');
      
      // Get liked items from localStorage (from the new simplified system)
      const savedLikedItems = localStorage.getItem('likedItems');
      let likedItems = [];
      
      if (savedLikedItems) {
        try {
          likedItems = JSON.parse(savedLikedItems);
          console.log('✅ Loaded liked items from localStorage:', likedItems.length, 'items');
        } catch (error) {
          console.error('❌ Error parsing liked items from localStorage:', error);
        }
      }

      // Convert to Set of product IDs
      const likedProductIds = new Set(likedItems.map(item => item.id));
      setLikedProducts(likedProductIds);
      
      console.log('✅ Liked products updated:', likedProductIds.size, 'products');
      console.log('📋 Liked product IDs:', Array.from(likedProductIds));
      
    } catch (error) {
      console.error('❌ Error fetching liked products:', error);
      setLikedProducts(new Set());
    } finally {
      setWishlistLoading(false);
    }
  };

  // Alternative method: Get all products and simulate liked products
  const fetchAllLikedProducts = async () => {
    try {
      console.log('🔄 Fetching all products to simulate liked products...');
      
      // Get all products and simulate some as liked for admin panel
      const allProducts = products || [];
      const mockLikedProducts = new Set();
      
      // Simulate liked products (first 5 products as liked for demo)
      allProducts.slice(0, 5).forEach(product => {
        mockLikedProducts.add(product._id || product.id);
      });
      
      setLikedProducts(mockLikedProducts);
      console.log('📝 Using simulated liked products for admin panel:', mockLikedProducts.size, 'items');
      console.log('📋 Simulated liked product IDs:', Array.from(mockLikedProducts));
      
    } catch (error) {
      console.error('❌ Error in alternative method:', error);
      // Final fallback - empty set
      setLikedProducts(new Set());
    }
  };

  // Products are now loaded automatically by the useProducts hook

  // loadProducts function removed - now handled by useProducts hook

  // Base products for stats/filters - team members see only their products
  const getMyProducts = () => {
    if (!isTeamMember || !currentUser) return products;
    const myId = String(currentUser._id || currentUser.id || '');
    if (!myId) return products;
    return products.filter(p => extractCreatorId(p.createdBy) === myId);
  };
  const myProducts = getMyProducts();

  const catalogProductTotal = useMemo(() => {
    if (isTeamMember) return myProducts.length;
    const pgTotal = Number(pagination?.total);
    if (Number.isFinite(pgTotal) && pgTotal > 0) return pgTotal;
    return myProducts.length;
  }, [isTeamMember, myProducts.length, pagination?.total]);

  const broadcastProductMenuCount = useCallback((count) => {
    if (!Number.isFinite(count) || count < 0) return;
    const detail = { count };
    window.dispatchEvent(new CustomEvent('adminProductMenuCount', { detail }));
    window.dispatchEvent(new CustomEvent('adminTeamProductMenuCount', { detail }));
  }, []);

  useEffect(() => {
    broadcastProductMenuCount(catalogProductTotal);
  }, [catalogProductTotal, broadcastProductMenuCount]);

  const lowStockProductsList = useMemo(
    () => myProducts.filter(isLowStockProduct),
    [products, isTeamMember, currentUser]
  );

  const scrollToProductGrid = () => {
    window.requestAnimationFrame(() => {
      productsGridTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const applyStockAlertFilterAll = () => {
    setStockFilter('low-stock');
    setStockAlertFilter('all');
    setCurrentProductPage(1);
    scrollToProductGrid();
  };

  const applyStockAlertFilterOne = (product) => {
    const id = productIdStr(product);
    if (!id) return;
    setStockFilter('low-stock');
    setStockAlertFilter(id);
    setCurrentProductPage(1);
    scrollToProductGrid();
  };

  const clearStockAlertFilter = () => {
    setStockAlertFilter(null);
    setStockFilter('all');
    setCurrentProductPage(1);
  };

  const quickStatCounts = useMemo(() => {
    return {
      total: catalogProductTotal,
      inStock: myProducts.filter(productHasAnyStock).length,
      active: myProducts.filter((p) => p.isActive !== false).length,
      featured: myProducts.filter((p) => readProductStatusFlags(p).isFeatured).length,
      new: myProducts.filter((p) => readProductStatusFlags(p).isNew).length,
      sale: myProducts.filter((p) => readProductStatusFlags(p).isOnSale).length,
    };
  }, [myProducts, catalogProductTotal]);

  const featuredProductsCount = quickStatCounts.featured;

  const quickStatFiltersClear =
    statusFilter === 'all' && stockFilter === 'all' && !stockAlertFilter;

  const toggleStatusQuickFilter = (value) => {
    setStatusFilter((prev) => (prev === value ? 'all' : value));
    setCurrentProductPage(1);
    scrollToProductGrid();
  };

  const toggleStockQuickFilter = (value) => {
    setStockFilter((prev) => (prev === value ? 'all' : value));
    setStockAlertFilter(null);
    setCurrentProductPage(1);
    scrollToProductGrid();
  };

  const clearQuickStatFilters = () => {
    setStatusFilter('all');
    setStockFilter('all');
    setStockAlertFilter(null);
    setCurrentProductPage(1);
    scrollToProductGrid();
  };

  const toggleFeaturedProductsFilter = () => toggleStatusQuickFilter('featured');

  const clearFeaturedProductsFilter = () => {
    setStatusFilter('all');
    setCurrentProductPage(1);
  };

  const renderProductQuickStats = (options = {}) => {
    const { compact = false } = options;
    const gridClass = compact
      ? 'products-quick-stat-grid products-quick-stat-grid--compact'
      : 'products-quick-stat-grid';

    const chips = [
      {
        key: 'total',
        label: 'Total',
        icon: 'fa-box',
        count: quickStatCounts.total,
        active: quickStatFiltersClear,
        onClick: () => {
          if (!quickStatFiltersClear) clearQuickStatFilters();
          else scrollToProductGrid();
        },
        variant: 'total',
        title: 'Show all products (clear quick filters)',
      },
      {
        key: 'in-stock',
        label: 'In stock',
        icon: 'fa-check-circle',
        count: quickStatCounts.inStock,
        active: stockFilter === 'in-stock',
        onClick: () => toggleStockQuickFilter('in-stock'),
        variant: 'stock',
        title: 'Toggle in-stock products only',
      },
      {
        key: 'ready',
        label: 'Ready',
        icon: wishlistLoading ? 'fa-spinner fa-spin' : 'fa-bolt',
        count: wishlistLoading ? '…' : quickStatCounts.active,
        active: statusFilter === 'active',
        onClick: () => toggleStatusQuickFilter('active'),
        variant: 'ready',
        title: 'Toggle active / ready products',
      },
      ...(isMainAdmin && allowFeaturedProduct
        ? [
            {
              key: 'featured',
              label: 'Featured',
              icon: 'fa-star',
              count: quickStatCounts.featured,
              active: statusFilter === 'featured',
              onClick: toggleFeaturedProductsFilter,
              variant: 'featured',
              title: 'Toggle featured products only',
            },
          ]
        : []),
      {
        key: 'new',
        label: 'New',
        icon: 'fa-sparkles',
        count: quickStatCounts.new,
        active: statusFilter === 'new',
        onClick: () => toggleStatusQuickFilter('new'),
        variant: 'new',
        title: 'Toggle new products',
      },
      {
        key: 'sale',
        label: 'Sale',
        icon: 'fa-tag',
        count: quickStatCounts.sale,
        active: statusFilter === 'sale',
        onClick: () => toggleStatusQuickFilter('sale'),
        variant: 'sale',
        title: 'Toggle on-sale products',
      },
    ];

    return (
      <div className={gridClass} role="group" aria-label="Product quick filters">
        {chips.map((chip) => (
          <button
            key={chip.key}
            type="button"
            className={`products-quick-stat-chip products-quick-stat-chip--${chip.variant}${
              chip.active ? ' is-active' : ''
            }`}
            onClick={chip.onClick}
            aria-pressed={chip.active}
            title={chip.title}
          >
            <span className="products-quick-stat-chip__head">
              <i className={`fas ${chip.icon}`} aria-hidden />
              <span className="products-quick-stat-chip__label">{chip.label}</span>
            </span>
            <span className="products-quick-stat-chip__value">{chip.count}</span>
          </button>
        ))}
      </div>
    );
  };

  // Enhanced Product Filtering Functions (from old admin panel)
  const getFilteredProductsByStock = () => {
    let filtered = products;
    
    console.log('Total products before filtering:', filtered.length);
    
    // 0. For team members: only show products they created (frontend safety filter - backend may return all)
    if (isTeamMember && currentUser) {
      const myId = String(currentUser._id || currentUser.id || '');
      if (myId) {
        filtered = filtered.filter(product => {
          const creatorId = extractCreatorId(product.createdBy);
          return creatorId === myId;
        });
      }
    }
    
    // 1. Apply search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(product => 
        product.name?.toLowerCase().includes(query) ||
        product.productId?.toLowerCase().includes(query) ||
        product.description?.toLowerCase().includes(query) ||
        (typeof product.category === 'string' ? product.category : product.category?.name || '').toLowerCase().includes(query) ||
        product.subcategory?.toLowerCase().includes(query)
      );
    }
    
    // 2. Apply category filter (IDs from catalog + sub/child ids on product)
    if (categoryFilter !== 'all') {
      filtered = filtered.filter((product) =>
        productMatchesAdminCategoryFilter(product, categoryFilter, adminCategoryTree)
      );
    }
    
    // 3. Apply status filter
    switch (statusFilter) {
      case 'active':
        filtered = filtered.filter(product => product.isActive !== false);
        break;
      case 'inactive':
        filtered = filtered.filter(product => product.isActive === false);
        break;
      case 'featured':
        filtered = filtered.filter((product) => readProductStatusFlags(product).isFeatured);
        break;
      case 'new':
        filtered = filtered.filter((product) => readProductStatusFlags(product).isNew);
        break;
      case 'sale':
        filtered = filtered.filter((product) => readProductStatusFlags(product).isOnSale);
        break;
      default:
        break;
    }
    
    // 4. Apply price filter
    switch (priceFilter) {
      case 'low':
        filtered = filtered.filter(product => product.price <= 3000);
        break;
      case 'medium':
        filtered = filtered.filter(product => product.price > 3000 && product.price <= 7000);
        break;
      case 'high':
        filtered = filtered.filter(product => product.price > 7000);
        break;
      default:
        break;
    }
    
    // 5. Apply createdBy filter (admin only - team members filtered in step 0)
    if (createdByFilter !== 'all') {
      const currentUserId = String(currentUser?._id || currentUser?.id || '');
      filtered = filtered.filter(product => {
        const creatorId = extractCreatorId(product.createdBy);
        if (createdByFilter === 'me') {
          return creatorId === currentUserId;
        }
        return creatorId === createdByFilter;
      });
    }

    // 6. Apply stock filter
    switch (stockFilter) {
      case 'out-of-stock':
        filtered = filtered.filter(product => {
          if (!product.stock) return true;
          if (!product.sizes) {
            return Object.values(product.stock).every(stock => stock === 0);
          }
          return Object.values(product.stock).every(colorStock => 
            Object.values(colorStock).every(stock => stock === 0)
          );
        });
        break;
      case 'low-stock':
        filtered = filtered.filter(product => {
          if (!product.stock) return false;
          if (!product.sizes) {
            return Object.values(product.stock).some(stock => stock > 0 && stock <= 5);
          }
          return Object.values(product.stock).some(colorStock => 
            Object.values(colorStock).some(stock => stock > 0 && stock <= 5)
          );
        });
        break;
      case 'in-stock':
        filtered = filtered.filter(product => {
          if (!product.stock) return false;
          if (!product.sizes) {
            return Object.values(product.stock).some(stock => stock > 0);
          }
          return Object.values(product.stock).some(colorStock => 
            Object.values(colorStock).some(stock => stock > 0)
          );
        });
        break;
      default:
        break;
    }

    // 7. Stock Alerts panel: narrow to selected low-stock product(s)
    if (stockAlertFilter === 'all') {
      const allowed = new Set(lowStockProductsList.map((p) => productIdStr(p)));
      filtered = filtered.filter((p) => allowed.has(productIdStr(p)));
    } else if (stockAlertFilter) {
      const id = stockAlertFilter;
      let narrowed = filtered.filter((p) => productIdStr(p) === id);
      if (narrowed.length === 0) {
        const fallback = myProducts.find((p) => productIdStr(p) === id);
        narrowed = fallback ? [fallback] : [];
      }
      filtered = narrowed;
    }

    // ✅ Liked filter removed as per requirement

    return filtered;
  };

  // Generate website product URL (from old admin panel)
  const generateWebsiteProductUrl = (product) => {
    console.log('🔗 Generating URL for product:', product);
    
    // Try different URL sources
    let productUrl = product.url || product.slug || product.link;
    
    // If no URL exists, build creator-based SEO slug (matches new storefront format)
    if (!productUrl) {
      const path = getProductStorefrontPath(product);
      productUrl = path && path !== '/' ? path.replace(/^\//, '') : '';
    }
    if (!productUrl) {
      const productId = product.productId || product._id || product.id || 'unknown';
      productUrl = `product/${productId}`;
    }
    
    // Ensure URL is complete
    if (!productUrl.startsWith('http')) {
      productUrl = `${window.location.origin}${productUrl.startsWith('/') ? '' : '/'}${productUrl}`;
    }
    
    console.log('🔗 Generated URL:', productUrl);
    return productUrl;
  };

  // Get category code (from old admin panel)
  const getCategoryCode = (category) => {
    const categoryMap = {
      'women': 'women',
      'men': 'men', 
      'kids': 'kids',
      'accessories': 'accessories',
      'fragrances': 'fragrances',
      'special': 'special'
    };
    return categoryMap[category?.toLowerCase()] || 'general';
  };

  const handleEditProduct = (product) => {
    console.log('Edit product:', product);
    // Use the onEditProduct prop to pass product data for editing
    if (onEditProduct) {
      onEditProduct(product);
    } else if (onNavigate) {
      onNavigate('add-product');
    }
  };

  const confirmDelete = async () => {
    if (!productToDelete) return;

    if (!canDeleteProduct(currentUser, productToDelete)) {
      setNotificationData({
        type: 'error',
        title: 'Delete Not Allowed',
        message: 'You do not have permission to delete this product.',
        duration: 5000,
      });
      setShowNotification(true);
      setShowDeleteConfirm(false);
      setProductToDelete(null);
      return;
    }

    const mongoId = getMongoProductId(productToDelete);
    if (!mongoId) {
      setNotificationData({
        type: 'error',
        title: 'Delete Failed',
        message: 'Invalid product ID.',
        duration: 5000,
      });
      setShowNotification(true);
      setShowDeleteConfirm(false);
      setProductToDelete(null);
      return;
    }

    try {
      console.log('🗑️ Deleting product:', mongoId);

      await deleteProduct(mongoId);
      console.log('✅ Product deleted successfully');
      
      // Show success notification
      setNotificationData({
        type: 'success',
        title: 'Product Deleted Successfully!',
        message: `"${productToDelete.name}" has been permanently removed from your store.`
      });
      setShowNotification(true);
      
    } catch (error) {
      const { message } = parseThrownAdminError(error);
      console.error('❌ Error deleting product:', message);

      setNotificationData({
        type: 'error',
        title: 'Delete Failed',
        message: `Failed to delete "${productToDelete.name}": ${message}`,
      });
      setShowNotification(true);
    } finally {
      // Close confirmation dialog and reset state
      setShowDeleteConfirm(false);
      setProductToDelete(null);
    }
  };

  const cancelDelete = () => {
    console.log('❌ Delete cancelled by user');
    setShowDeleteConfirm(false);
    setProductToDelete(null);
  };

  const handleStatusToggle = async (productId, statusType) => {
    try {
      console.log('🔄 Toggling status:', productId, statusType);

      if (statusType === 'isFeatured' && !allowFeaturedProduct) {
        setNotificationData({
          type: 'error',
          title: 'Not allowed',
          message: 'Only the store admin can mark products as Featured.',
          duration: 4000,
        });
        setShowNotification(true);
        return;
      }

      // Find the product to get current status
      const product = products.find(p => (p.id || p._id) === productId);
      if (!product) {
        alert('Product not found');
        return;
      }

      // Determine new status value
      let newValue;
      switch (statusType) {
        case 'isFeatured':
          newValue = !product.isFeatured;
          break;
        case 'isNew':
          newValue = !product.isNew;
          break;
        case 'isOnSale':
          newValue = !product.isOnSale;
          break;
        case 'isActive':
          newValue = product.isActive === false ? true : false;
          break;
        default:
          return;
      }

      // Use status-only endpoint for better performance (only updates status flags)
      const token = localStorage.getItem('adminToken');
      if (!token) {
        throw new Error('No admin token found');
      }

      // Use dedicated status endpoint
      const response = await fetch(getApiUrl(`/admin/products/status/${productId}`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ [statusType]: newValue })
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.message || 'Failed to update product status');
      }
      
      // Refresh products list
      await refetch();
      
      console.log('✅ Status updated successfully');
      
      // Show success notification
      setNotificationData({
        type: 'success',
        title: 'Status Updated',
        message: `Product ${statusType} status has been ${newValue ? 'enabled' : 'disabled'}.`,
        duration: 3000
      });
      setShowNotification(true);
    } catch (error) {
      console.error('❌ Error updating product status:', error);
      
      // Show error notification
      setNotificationData({
        type: 'error',
        title: 'Update Failed',
        message: `Failed to update product status: ${error.message}`,
        duration: 5000
      });
      setShowNotification(true);
    }
  };

  // Handle opening sale settings modal
  const handleOpenSaleSettings = (product) => {
    setSelectedProductForSale(product);
    setShowSaleSettings(true);
  };

  // Handle closing sale settings modal
  const handleCloseSaleSettings = () => {
    setShowSaleSettings(false);
    setSelectedProductForSale(null);
  };

  // Handle saving sale settings
  const handleSaveSaleSettings = async (productId, saleData) => {
    try {
      console.log('💾 Saving sale settings:', productId, saleData);

      const token = localStorage.getItem('adminToken');
      if (!token) {
        throw new Error('No admin token found');
      }

      // Update product with sale settings
      const response = await fetch(getApiUrl(`/admin/products/${productId}`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(saleData)
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.message || 'Failed to update sale settings');
      }
      
      // Refresh products list
      await refetch();
      
      console.log('✅ Sale settings updated successfully');
      
      // Show success notification
      setNotificationData({
        type: 'success',
        title: 'Sale Settings Updated',
        message: `Product sale settings have been ${saleData.isOnSale ? 'enabled' : 'disabled'} successfully.`,
        duration: 3000
      });
      setShowNotification(true);
    } catch (error) {
      console.error('❌ Error updating sale settings:', error);
      throw error; // Re-throw to let modal handle it
    }
  };

  if (loading) {
    return (
      <AdminLoading
        title="Loading Products"
        subtitle="Fetching products from database..."
        icon="📦"
      />
    );
  }

  return (
    <div className="products-container">
      {/* Products Header */}
      <header className="products-page-header" aria-labelledby="products-page-title">
        <div className="products-page-header__surface">
          <div className="products-page-header__accent" aria-hidden="true" />
          <div className="products-page-header__main">
            <div className="products-page-header__icon-wrap" aria-hidden="true">
              <i className="fas fa-layer-group products-page-header__icon" />
            </div>
            <div className="products-page-header__copy">
              <span className="products-page-header__eyebrow">
                {isTeamMember ? 'Your catalog' : 'Admin · Catalog'}
              </span>
              <h1 id="products-page-title" className="products-page-header__title">
                {isTeamMember ? 'Your Products' : 'Products Management'}
              </h1>
              <p className="products-page-header__subtitle">
                {isTeamMember
                  ? `${catalogProductTotal.toLocaleString()} product${catalogProductTotal === 1 ? '' : 's'} in your catalog`
                  : `${catalogProductTotal.toLocaleString()} product${catalogProductTotal === 1 ? '' : 's'} in the store catalog — browse, filter, and update inventory.`}
              </p>
            </div>
          </div>
          <div className="products-page-header__actions">
            {hasAccess('Products|Create', currentUser) && (
              <button
                type="button"
                className="products-page-header__cta btn-primary"
                onClick={() => {
                  if (onNavigate) {
                    onNavigate('add-product');
                  }
                }}
              >
                <i className="fas fa-plus" aria-hidden="true" />
                Add New Product
              </button>
            )}
          </div>
        </div>
      </header>

      <div className="content-card">
        {/* Compact Search & Filter System */}
        <div className="compact-filter-section">
          {/* Search Bar */}
          <div className="search-bar">
            <div className="search-input-group">
              <input
                type="text"
                placeholder="Search products by name, ID, description, category..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
              {searchQuery && (
                <button 
                  className="clear-search"
                  onClick={() => setSearchQuery('')}
                  title="Clear search"
                >
                  <i className="fas fa-times"></i>
                </button>
              )}
            </div>
          </div>

          {/* Advanced filters */}
          <div className="compact-filters">
            <div className="products-advanced-filters">
              <div className="products-advanced-filters__grid">
                <label className="products-filter-field">
                  <span className="products-filter-field__label">
                    <i className="fas fa-boxes-stacked" aria-hidden />
                    Stock
                  </span>
                  <select
                    value={stockFilter}
                    onChange={(e) => {
                      setStockFilter(e.target.value);
                      setStockAlertFilter(null);
                      setCurrentProductPage(1);
                    }}
                    className="products-filter-field__control"
                  >
                    <option value="all">All Stock</option>
                    <option value="in-stock">In Stock</option>
                    <option value="out-of-stock">Out of Stock</option>
                    <option value="low-stock">Low Stock</option>
                  </select>
                </label>

                <label className="products-filter-field">
                  <span className="products-filter-field__label">
                    <i className="fas fa-layer-group" aria-hidden />
                    Category
                  </span>
                  <select
                    value={categoryFilter}
                    onChange={(e) => {
                      setCategoryFilter(e.target.value);
                      setCurrentProductPage(1);
                    }}
                    className="products-filter-field__control"
                  >
                    <option value="all">All Categories</option>
                    {adminCategoryFilterOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="products-filter-field">
                  <span className="products-filter-field__label">
                    <i className="fas fa-sliders" aria-hidden />
                    Status
                  </span>
                  <select
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setCurrentProductPage(1);
                    }}
                    className="products-filter-field__control"
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    {allowFeaturedProduct && <option value="featured">Featured</option>}
                    <option value="new">New Products</option>
                    <option value="sale">On Sale</option>
                  </select>
                </label>

                <label className="products-filter-field">
                  <span className="products-filter-field__label">
                    <i className="fas fa-tags" aria-hidden />
                    Price
                  </span>
                  <select
                    value={priceFilter}
                    onChange={(e) => {
                      setPriceFilter(e.target.value);
                      setCurrentProductPage(1);
                    }}
                    className="products-filter-field__control"
                  >
                    <option value="all">All Prices</option>
                    <option value="low">Low (≤ Rs. 3,000)</option>
                    <option value="medium">Medium (Rs. 3,001–7,000)</option>
                    <option value="high">High (&gt; Rs. 7,000)</option>
                  </select>
                </label>

                {!isTeamMember && (
                  <label className="products-filter-field">
                    <span className="products-filter-field__label">
                      <i className="fas fa-user-pen" aria-hidden />
                      Creator
                    </span>
                    <select
                      value={createdByFilter}
                      onChange={(e) => {
                        setCreatedByFilter(e.target.value);
                        setCurrentProductPage(1);
                      }}
                      className="products-filter-field__control"
                      title="Filter by product creator"
                    >
                      <option value="all">All Creators</option>
                      <option value="me">Mine</option>
                      {(() => {
                        const creatorsMap = new Map();
                        (products || []).forEach((p) => {
                          const cb = p.createdBy;
                          if (!cb) return;
                          const id =
                            typeof cb === 'object' && cb._id ? String(cb._id) : String(cb);
                          const name =
                            typeof cb === 'object' && cb.name
                              ? cb.name
                              : getCreatorDisplayName(cb) || 'Unknown';
                          if (id && !creatorsMap.has(id)) creatorsMap.set(id, name);
                        });
                        const currentUserId = String(currentUser?._id || currentUser?.id || '');
                        return Array.from(creatorsMap.entries())
                          .filter(([id]) => id !== currentUserId)
                          .map(([id, name]) => (
                            <option key={id} value={id}>
                              {name}
                            </option>
                          ));
                      })()}
                    </select>
                  </label>
                )}

                <div className="products-filter-field products-filter-field--action">
                  <span className="products-filter-field__label products-filter-field__label--spacer" aria-hidden>
                    &nbsp;
                  </span>
                  <button
                    type="button"
                    className="products-filter-clear-all"
                    onClick={() => {
                      setStockFilter('all');
                      setStatusFilter('all');
                      setCategoryFilter('all');
                      setPriceFilter('all');
                      setCreatedByFilter('all');
                      setSearchQuery('');
                      setStockAlertFilter(null);
                      setCurrentProductPage(1);
                    }}
                    title="Clear all filters"
                  >
                    <i className="fas fa-rotate-left" aria-hidden />
                    Clear all
                  </button>
                </div>
              </div>
            </div>

            {renderProductQuickStats()}
          </div>
        </div>

        <div className="products-grid-wrapper" ref={productsGridTopRef}>
          {stockAlertFilter && (
            <div className="products-stock-alert-banner" role="status">
              <span>
                <i className="fas fa-filter" aria-hidden />
                {stockAlertFilter === 'all'
                  ? 'Showing low-stock alert products only.'
                  : 'Showing selected alert product only.'}
              </span>
              <button type="button" className="products-stock-alert-banner-clear" onClick={clearStockAlertFilter}>
                Clear alert filter
              </button>
            </div>
          )}
          {isMainAdmin && statusFilter === 'featured' && (
            <div className="products-featured-filter-banner" role="status">
              <span>
                <i className="fas fa-star" aria-hidden />
                Showing featured products only ({getFilteredProductsByStock().length}).
              </span>
              <button
                type="button"
                className="products-featured-filter-banner-clear"
                onClick={clearFeaturedProductsFilter}
              >
                Clear featured filter
              </button>
            </div>
          )}
          {/* ✅ Show filter stats in search results */}
          {searchQuery && (
            <div className="search-results-stats">
              {renderProductQuickStats({ compact: true })}
            </div>
          )}
          
          <div className="products-grid">
            {(() => {
              const filteredProducts = getFilteredProductsByStock();
              if (filteredProducts.length === 0) {
                return (
                  <div className="products-empty-state">
                    <div className="empty-icon">📦</div>
                    <h3>{isTeamMember ? "You haven't added any products yet" : 'No products found'}</h3>
                    <p>{isTeamMember ? 'Add your first product to see it here. Only products you create will appear in your list.' : 'Add products or adjust your filters.'}</p>
                    {isTeamMember && hasAccess('Products|Create', currentUser) && onNavigate && (
                      <button className="btn-primary" onClick={() => onNavigate('add-product')}>
                        <i className="fas fa-plus"></i> Add Your First Product
                      </button>
                    )}
                  </div>
                );
              }
              const startIndex = (currentProductPage - 1) * productsPerPage;
              const endIndex = startIndex + productsPerPage;
              // Sort products by most recent first (assuming higher ID = more recent)
              const sortedProducts = [...filteredProducts].sort((a, b) => (b.id || b._id || 0) - (a.id || a._id || 0));
              const paginatedProducts = sortedProducts.slice(startIndex, endIndex);
              
              return paginatedProducts.map(product => {
                // Debug: Log product data for each product
                console.log('🔍 Product data for rendering:', {
                  id: product.id,
                  _id: product._id,
                  productId: product.productId,
                  name: product.name,
                  url: product.url,
                  datePosted: product.datePosted,
                  mainCategory: product.mainCategory
                });
                
                return (
                  <AdminProductCard
                    key={product.id || product._id}
                    product={product}
                    onEdit={handleEditProduct}
                    onDelete={
                      canDeleteProduct(currentUser, product)
                        ? (p) => handleDeleteProduct(p, p.name, p.images)
                        : undefined
                    }
                    onOpenInventoryHub={handleOpenInventoryHub}
                    onManageReviews={(p) => {
                      setSelectedProductForReviews(p);
                      setShowReviewsModal(true);
                    }}
                    reviewsReadOnly={!isMainAdmin}
                    onStatusToggle={handleStatusToggle}
                    onSaleSettings={handleOpenSaleSettings}
                    canToggleFeatured={allowFeaturedProduct}
                  />
                );
              });
            })()}
          </div>
        </div>

        {/* Pagination Controls */}
        {(() => {
          // Calculate filtered products count for pagination
          const filteredProducts = getFilteredProductsByStock();
          const filteredProductsCount = filteredProducts.length;
          const totalPages = Math.ceil(filteredProductsCount / productsPerPage);
          
          // Only show pagination if there are more products than can fit on one page
          if (filteredProductsCount <= productsPerPage) {
            return null;
          }
          
          return (
            <div className="pagination-controls">
              <div className="pagination-info">
                Showing {((currentProductPage - 1) * productsPerPage) + 1} to {Math.min(currentProductPage * productsPerPage, filteredProductsCount)} of {filteredProductsCount} products
              </div>
              <div className="pagination-buttons">
                <button 
                  className="pagination-btn"
                  onClick={() => setCurrentProductPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentProductPage === 1}
                >
                  <i className="fas fa-chevron-left"></i>
                  Previous
                </button>
                
                {(() => {
                  const pages = [];
                  
                  for (let i = 1; i <= totalPages; i++) {
                    if (i === 1 || i === totalPages || (i >= currentProductPage - 1 && i <= currentProductPage + 1)) {
                      pages.push(
                        <button
                          key={i}
                          className={`pagination-btn ${i === currentProductPage ? 'active' : ''}`}
                          onClick={() => setCurrentProductPage(i)}
                        >
                          {i}
                        </button>
                      );
                    } else if (i === currentProductPage - 2 || i === currentProductPage + 2) {
                      pages.push(<span key={i} className="pagination-dots">...</span>);
                    }
                  }
                  
                  return pages;
                })()}
                
                <button 
                  className="pagination-btn"
                  onClick={() => setCurrentProductPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentProductPage === totalPages}
                >
                  Next
                  <i className="fas fa-chevron-right"></i>
                </button>
              </div>
            </div>
          );
        })()}

        {/* Stock Alerts — bottom of Products Management; click filters grid above */}
        {lowStockProductsList.length > 0 && (
          <div className="products-stock-alerts-section analytics-section stock-alerts-section">
            <div className="stock-alerts-card">
              <div className="stock-alerts-header">
                <div className="stock-alerts-title">
                  <div className="stock-alerts-icon">
                    <i className="fas fa-exclamation-triangle" aria-hidden />
                  </div>
                  <div>
                    <h2>Stock Alerts</h2>
                    <p>Products running low on inventory – click to filter the list above</p>
                  </div>
                </div>
                <button
                  type="button"
                  className={`stock-alerts-badge stock-alerts-badge-btn${stockAlertFilter === 'all' ? ' stock-alerts-badge-btn--active' : ''}`}
                  onClick={applyStockAlertFilterAll}
                  title="Show all low-stock products in the grid above"
                >
                  {lowStockProductsList.length} product{lowStockProductsList.length !== 1 ? 's' : ''}
                </button>
              </div>
              <div className="low-stock-products">
                <h3 className="low-stock-subtitle">Low stock products</h3>
                <div className="low-stock-list low-stock-list--scroll">
                  {lowStockProductsList.map((product) => {
                    const totalStock = getTotalStock(product);
                    const displayName =
                      typeof product?.name === 'string' || typeof product?.name === 'number'
                        ? product.name
                        : '-';
                    const isCritical = totalStock <= 2;
                    const productImage = getPrimaryProductImageUrl(product);
                    const pid = productIdStr(product);
                    const isSelected = stockAlertFilter === pid;
                    return (
                      <button
                        key={pid || displayName}
                        type="button"
                        className={`low-stock-item low-stock-item--clickable ${isCritical ? 'critical' : ''} ${isSelected ? 'low-stock-item--selected' : ''}`}
                        onClick={() => applyStockAlertFilterOne(product)}
                      >
                        <div className="low-stock-item-image">
                          {productImage ? (
                            <img src={productImage} alt="" />
                          ) : (
                            <div className="low-stock-placeholder">
                              <i className="fas fa-box" aria-hidden />
                            </div>
                          )}
                        </div>
                        <div className="low-stock-item-info">
                          <span className="low-stock-product-name">{displayName}</span>
                          <span className={`low-stock-badge ${isCritical ? 'critical' : 'warning'}`}>
                            <i className="fas fa-layer-group" aria-hidden />
                            {totalStock} left
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Golden Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        isOpen={showDeleteConfirm}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        productName={productToDelete?.name}
        productId={productToDelete?.id}
      />

      {/* Golden Success/Error Notification */}
      <GoldenNotification
        isVisible={showNotification}
        onClose={() => setShowNotification(false)}
        type={notificationData.type}
        title={notificationData.title}
        message={notificationData.message}
        duration={4000}
      />

      {inventoryHubProduct && (
        <ProductImagesStockHubModal
          product={inventoryHubProduct}
          currentUser={currentUser}
          onClose={handleCloseInventoryHub}
          onStockUpdated={handleStockUpdated}
        />
      )}

      {/* Sale Settings Modal */}
      {showSaleSettings && selectedProductForSale && (
        <SaleSettingsModal
          product={selectedProductForSale}
          onClose={handleCloseSaleSettings}
          onSave={handleSaveSaleSettings}
        />
      )}

      {/* Customer Reviews Modal */}
      {showReviewsModal && selectedProductForReviews && (
        <CustomerReviewsModal
          product={selectedProductForReviews}
          canEditReviews={isMainAdmin}
          onClose={() => { setShowReviewsModal(false); setSelectedProductForReviews(null); }}
        />
      )}
    </div>
  );
};

export default Products;